import "@/lib/signupAcquisitionBrowser";
import { createRoot } from "react-dom/client";
import { persistQueryClientRestore, persistQueryClientSubscribe } from "@tanstack/react-query-persist-client";
import App, { queryClient } from "./App.tsx";
import { CONFIG } from "@/config";
import { persistence as unitCachePersistence } from "@/lib/cachePersistence";
import { tokenManager } from "@/utils/token-manager";
import { readStackLedger, reportUnexpectedReload } from "@/lib/native/memoryLedger";
import "./index.css";
import "./styles/ios-status-bar.css";
import "./styles/animations.css";
import * as Sentry from "@sentry/react";
import { IS_CONNECTION_SESSION } from "@/utils/connection-session";
import { initializePushListeners } from "@/utils/push-notifications";
import {
  dropConsoleFrames,
  sanitizeTelemetryEnvelope,
  sanitizeTelemetryEvent,
  sanitizeTelemetryLog,
} from "@/utils/telemetryPrivacy";
// PostHog singleton — initializes on import (no-op in dev or when VITE_POSTHOG_KEY is unset).
// Must be imported before <App /> mounts so $pageview fires from the first route transition.
import "@/services/analytics.service";

// Enhanced Sentry initialization with comprehensive error tracking
// Only initialize Sentry in production/staging environments
if (!CONFIG.IS_DEVELOPMENT && !IS_CONNECTION_SESSION) {
  Sentry.init({
    dsn: "https://fedb6a6183c80851055ccc4602f1d9c5@o4510431466684416.ingest.us.sentry.io/4510431468060672",

    // Environment configuration
    environment: CONFIG.API_ENV || (CONFIG.IS_PRODUCTION ? "production" : "development"),
    release: `stasht@${import.meta.env.VITE_APP_VERSION || "0.0.0"}`,

    // PII and privacy settings. Errors still carry the user id from AuthContext;
    // IP addresses, cookies and request bodies are not sent.
    sendDefaultPii: false,

    // Performance monitoring
    tracesSampleRate: CONFIG.IS_PRODUCTION ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Console logging integration - captures console.error and console.warn
      Sentry.consoleLoggingIntegration({
        levels: ["error", "warn"],
      }),

      // Session replay is added once the page is idle (below), not here.
    ],

    // Session replay configuration (read by the replay integration when it is added)
    replaysSessionSampleRate: CONFIG.IS_PRODUCTION ? 0.1 : 1.0, // 10% of sessions in prod, 100% in staging
    replaysOnErrorSampleRate: 1.0, // Always record replays when errors occur

    // Enable structured logging
    enableLogs: true,

    // Filter out known non-critical errors
    beforeSend(event, hint) {
      // Filter out browser extension errors
      if (event.exception) {
        const error = hint.originalException;
        if (error instanceof Error) {
          // Filter out common browser extension errors
          if (
            error.message?.includes("chrome-extension://") ||
            error.message?.includes("moz-extension://") ||
            error.message?.includes("safari-extension://")
          ) {
            return null;
          }
        }
      }
      return sanitizeTelemetryEvent(event);
    },
    beforeSendTransaction: event => sanitizeTelemetryEvent(event),
    beforeSendLog: log => sanitizeTelemetryLog(log),
    // Replays skip beforeSend. One that cannot be scrubbed is not sent.
    transport: options => {
      const transport = Sentry.makeFetchTransport(options);
      return {
        ...transport,
        send: envelope =>
          sanitizeTelemetryEnvelope(envelope).then(
            clean => transport.send(clean),
            () => ({}),
          ),
      };
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extension errors
      "chrome-extension://",
      "moz-extension://",
      "safari-extension://",
      // Network errors that are expected
      "NetworkError",
      "Failed to fetch",
      // ResizeObserver errors (common and non-critical)
      "ResizeObserver loop limit exceeded",
      // Chunk loading errors (expected after deployments, handled by auto-refresh)
      "Failed to fetch dynamically imported module",
      "Loading chunk",
      "text/html",
      "ChunkLoadError",
    ],
  });

  // Session replay is ~120 KB; it loads in its own chunk once the page is idle
  // instead of in the boot bundle. The sample rates above still apply; an error
  // before it loads is reported without a replay.
  const loadReplay = () => {
    void import("@sentry-internal/replay")
      .then(({ replayIntegration }) => {
        // Mask all text content and user input for privacy
        Sentry.addIntegration(
          replayIntegration({ maskAllText: true, blockAllMedia: true, beforeAddRecordingEvent: dropConsoleFrames }),
        );
      })
      .catch(() => {});
  };
  // Safari has no requestIdleCallback.
  if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(loadReplay, { timeout: 5000 });
  else setTimeout(loadReplay, 3000);
}

// Handle chunk loading errors (happens after new deployments when old chunks are gone)
// Vite fires this event when dynamic imports fail
window.addEventListener("vite:preloadError", (event: Event) => {
  // Prevent the error from being logged to console
  event.preventDefault();

  // Check if we've already tried refreshing recently (prevent infinite loops)
  const lastRefresh = sessionStorage.getItem("chunk_error_refresh");
  const now = Date.now();

  if (lastRefresh && now - parseInt(lastRefresh, 10) < 10000) {
    // Already refreshed within the last 10 seconds, don't loop
    console.warn("Chunk loading failed after refresh, showing error page");
    return;
  }

  // Mark that we're refreshing
  sessionStorage.setItem("chunk_error_refresh", String(now));

  // Auto-refresh to get the new version
  window.location.reload();
});

// Also catch generic chunk load errors that might not fire vite:preloadError
window.addEventListener("error", event => {
  if (
    event.message?.includes("Failed to fetch dynamically imported module") ||
    event.message?.includes("Loading chunk") ||
    event.message?.includes("text/html")
  ) {
    const lastRefresh = sessionStorage.getItem("chunk_error_refresh");
    const now = Date.now();

    if (!lastRefresh || now - parseInt(lastRefresh, 10) > 10000) {
      sessionStorage.setItem("chunk_error_refresh", String(now));
      window.location.reload();
    }
  }
});

// Set up global error handlers for unhandled errors and promise rejections
// These complement Sentry's automatic error capture (only in non-dev environments)
if (!CONFIG.IS_DEVELOPMENT) {
  window.addEventListener("error", event => {
    // Only capture if it's not already handled by Sentry's React integration
    if (event.error && !event.error._sentryHandled) {
      Sentry.captureException(event.error, {
        contexts: {
          error: {
            type: "window.error",
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        },
      });
    }
  });

  window.addEventListener("unhandledrejection", event => {
    // Capture unhandled promise rejections
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    Sentry.captureException(error, {
      contexts: {
        error: {
          type: "unhandledrejection",
          reason: event.reason,
        },
      },
    });
  });
}

// Initialize debug logger (simplified version without fetch interception)
import "./utils/debug-logger";

initializePushListeners();

// Only initialize performance monitoring in development
if (CONFIG.IS_DEVELOPMENT) {
  import("./services/performance.service")
    .then(() => {
      console.log("Performance monitoring initialized");
    })
    .catch(err => {
      console.warn("Performance monitoring not available:", err);
    });
}

// What the page was carrying before this boot, read before the stack mounts
// and overwrites it.
const ledgerBeforeBoot = readStackLedger();

const restoreApp = async () => {
  const persistence = unitCachePersistence(queryClient);

  // The disk cache belongs to a signed-in session: paint from it on a
  // signed-in cold start; never read it for welcome, login, or signup.
  if (tokenManager.hasSessionMarker()) {
    try {
      await persistQueryClientRestore(persistence);
    } catch {
      // A broken disk snapshot was already discarded. Start with an empty cache.
    }
  }

  persistQueryClientSubscribe(persistence);
  createRoot(document.getElementById("root")!).render(<App />);
  // The content process may have been replaced under memory pressure; say so
  // once analytics has had a moment to initialise.
  window.setTimeout(() => reportUnexpectedReload(ledgerBeforeBoot), 5000);
};

void restoreApp();
// autodeploy test Sat Jan 24 15:18:10 PST 2026
